import { h, defineComponent, ref, watch, VNodeChildren } from 'vue';
import { d, t } from '@/ui/i18n/i18n';
import * as qs from '@/ui/queryState';
import * as q from '@/ui/routes/query';
import { box, level, loader, namedLink, pageTitle, pagination, sortableTextColumn, textColumn } from '@/ui/components';
import { issueList, issueListExportUrl, SupportedExportFormat } from '@/services/api/resources/analytics';
import { AnalyticsEntityGroup, emptyDetailList, Issue, IssueList } from '@/entities/analytics';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { hasFeature, hasPermission, Permissions, user } from '@/ui/auth/auth';
import { AnalyticsTable } from '@/ui/components/table/AnalyticsTable';
import { SortDirection } from '@/ui/types';
import { IssueGroupBy } from '@/services/api/resources/analytics/types';
import { IssueStatus } from '@/entities/todo';
import { IssuePriority, IssueType } from '@/entities/issue';
import { download } from '@/ui/helpers/uiHelper';
import { exportDropdown } from '@/ui/components/dropdown/ExportDropdown';
import { useQueryFiltering } from './useQueryFilters';
import { renderForbiddenPage } from '../../error/ForbiddenPage';
import { issueEditPageLink } from '../../issue/IssueEditPage';
import { renderAnalyticsTeaserPage } from '../../teasers/AnalyticsTeaserPage';
import { BackButton } from '@/ui/components/button/BackButton';

export const IssuesDetailPage = defineComponent({
  name: 'IssuesDetailPage',
  setup() {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir } = useSorting('analyticsDate', SortDirection.Descending);
    const {
      start,
      end,
      creator,
      assignee,
      resolver,
      question,
      entityItem,
      group,
      category,
      checklist,
      section,
      inspection,
      sensor,
      type,
      priority,
      response,
      questionTitle,
      status,
      overdue,
    } = useQueryFiltering();

    // get list task
    const query = () => ({
      timezone: user.value.timezone,
      page: page.value,
      perPage: perPage.value,
      sort: sort.value,
      dir: dir.value,
      start: start.value,
      end: end.value,
      creator: creator.value,
      assignee: assignee.value,
      resolver: resolver.value,
      question: question.value,
      entityItem: entityItem.value,
      group: group.value,
      category: category.value,
      checklist: checklist.value,
      section: section.value,
      inspection: inspection.value,
      sensor: sensor.value,
      type: type.value,
      priority: priority.value,
      response: response.value,
      questionTitle: questionTitle.value,
      status: status.value,
      overdue: overdue.value,
    });
    const list = ref<IssueList>(emptyDetailList);
    const getList = useTask(
      () => issueList(query()),
      issues => {
        list.value = issues;
        initialLoading.value = false;
      },
    );
    watch([
      page,
      perPage,
      group,
      entityItem,
      assignee,
      category,
      checklist,
      section,
      inspection,
      sensor,
      type,
      priority,
      response,
      question,
      questionTitle,
      status,
      sort,
      dir,
    ], getList.run);

    // export task
    const { run: exportData } = useTask(
      (format: SupportedExportFormat | undefined) => issueListExportUrl(
        query(),
        format as SupportedExportFormat,
        user.value.locale,
      ),
      download,
    );

    return function render() {
      return h('div', [
        h('div', [
          h('div', [
            level(
              [h(BackButton), pageTitle(t('common.issues'))],
              [exportDropdown(exportData)],
            ),
            box([
              loader('div', getList,
                [
                  h(AnalyticsTable, {
                    props: {
                      list: list.value.data,
                      sort: sort.value,
                      dir: dir.value,
                    },
                    scopedSlots: {
                      default: () => [
                        sortableTextColumn({
                          field: 'createdAt',
                          label: t('common.created-at'),
                        }, {
                          default: ({ row }: { row: Issue }) =>
                            row.data.createdAt ? d(row.data.createdAt) : '',
                        }),
                        textColumn({
                          field: 'title',
                          label: t('common.title'),
                        }, {
                          default: ({ row }: { row: Issue }) => row.authorizedTo.update
                            ? [issueEditPageLink(row.data.title, row.id)]
                            : row.data.title,
                        }),
                        textColumn({
                          field: 'entityItem',
                          label: t('common.entity-item'),
                        }, {
                          default: ({ row }: { row: Issue }) =>
                            row.data.entityItem ? row.data.entityItem.data.title : '',
                        }),
                      ],
                    },
                    on: {
                      sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                        resetPagination();
                        sort.value = field;
                        dir.value = direction;
                      }),
                    },
                  }),
                  (list.value.meta.total)
                    ? pagination({
                      current: page.value,
                      total: list.value.meta.total,
                      perPage: perPage.value,
                    })
                    : null,
                ], !initialLoading.value),
            ]),
          ]),
        ]),
      ]);
    };
  },
});

interface Query {
  start: Date;
  end: Date;
  creator: number[];
  assignee: number[];
  resolver: number[];
  entityItem: number[];
  group: number[];
  category: number[];
  checklist: number[];
  question: string[];
  questionTitle: string[];
  response: string[];
  inspection: number[];
  section: string[];
  sensor: number[];
  type: IssueType[];
  priority: IssuePriority[];
  status: IssueStatus[];
}

export const issuesDetailPageLink = (
  anchor: VNodeChildren,
  groupBy: IssueGroupBy,
  group: AnalyticsEntityGroup,
  query: Query,
) => namedLink(
  anchor,
  'analyticsIssuesExplain',
  undefined,
  {
    start: q.dateEncoder(group.start || query.start),
    end: q.dateEncoder(group.end || query.end),
    creator: groupBy === IssueGroupBy.Creator
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.creator),
    assignee: groupBy === IssueGroupBy.Assignee
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.assignee),
    resolver: groupBy === IssueGroupBy.Resolver
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.resolver),
    entityItem: groupBy === IssueGroupBy.EntityItem
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.entityItem),
    group: groupBy === IssueGroupBy.Group
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.group),
    category: groupBy === IssueGroupBy.Category
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.category),
    checklist: groupBy === IssueGroupBy.Checklist
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.checklist),
    question: groupBy === IssueGroupBy.Question
      ? q.nullableStringEncoder(group.id)
      : q.stringsEncoder(query.question),
    questionTitle: q.stringsEncoder(query.questionTitle),
    response: q.stringsEncoder(query.response),
    inspection: groupBy === IssueGroupBy.Inspection
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.inspection),
    section: groupBy === IssueGroupBy.Section
      ? q.nullableStringEncoder(group.id)
      : q.stringsEncoder(query.section),
    sensor: groupBy === IssueGroupBy.Sensor
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.sensor),
    type: groupBy === IssueGroupBy.Type
      ? q.nullableStringEncoder(group.id)
      : q.stringEnumsEncoder(query.type),
    priority: groupBy === IssueGroupBy.Priority
      ? q.nullableStringEncoder(group.id)
      : q.numericEnumsEncoder(query.priority),
    status: q.numericEnumsEncoder(query.status),
  },
);
